import { type FC } from 'react';

import { Heading, NotificationBox, Stack, Text } from '@eneco/sparky';
import type { FieldValues, UseFormHandleSubmit } from 'react-hook-form';

import { OfferItemCard, type OfferItemCardProps } from './OfferItemCard';
import { Header, Layout, NextButton } from '../../components';

export interface ProductOfferStepItem extends OfferItemCardProps {
  id: string;
  /** Heading rendered below this card, e.g. address-specific info copy. */
  infoTitle?: string;
  infoText?: string;
}

export interface ProductOfferStepNotification {
  title?: string;
  text?: string;
  variant?: 'info' | 'success' | 'warning' | 'error' | 'chat';
}

export interface ProductOfferStepProps {
  title?: string;
  text?: string;
  isLoading?: boolean;
  handleSubmit?: UseFormHandleSubmit<FieldValues>;
  isError?: boolean;
  errorTitle?: string;
  errorText?: string;
  errorVariant?: 'info' | 'success' | 'warning' | 'error' | 'chat';
  nextStepText?: string;
  isSubmitSuccessful?: boolean;
  /** The product cards — dongle/bundle-specific, supplied by the flow. */
  items: ProductOfferStepItem[];
  /** Extra notifications shown below the cards, e.g. based on the customer's selection. */
  notifications?: ProductOfferStepNotification[];
}

/**
 * Presentational shell shared by flows whose offer page is "product cards + next button"
 * (dongle, bundle). Holds no data-fetching or flow-context logic itself; the flow keeps that
 * and supplies its product cards and notifications as props.
 */
export const ProductOfferStep: FC<ProductOfferStepProps> = ({
  title,
  text,
  isLoading,
  handleSubmit,
  isError,
  errorTitle,
  errorText,
  errorVariant,
  nextStepText,
  isSubmitSuccessful,
  items,
  notifications,
}) => (
  <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
    <Header>
      <Header.Title>{title}</Header.Title>
      {text && <Header.Text>{text}</Header.Text>}
    </Header>

    {isError ? (
      <NotificationBox isAlert title={errorTitle} text={errorText} variant={errorVariant} />
    ) : (
      <Stack gap="6">
        {items.map(({ id, infoTitle, infoText, ...item }) => (
          <Stack key={id} gap="6">
            <OfferItemCard {...item} />
            {infoTitle && (
              <Heading as="h3" size="3XS">
                {infoTitle}
              </Heading>
            )}
            {infoText &&
              (infoTitle ? (
                <Text>{infoText}</Text>
              ) : (
                <Heading as="h3" size="3XS">
                  <Text weight="bold">{infoText}</Text>
                </Heading>
              ))}
          </Stack>
        ))}

        {notifications?.map((notification, index) => (
          <NotificationBox
            key={index}
            isAlert
            title={notification.title}
            text={notification.text}
            variant={notification.variant}
          />
        ))}

        <Stack direction={{ initial: 'column', md: 'row' }}>
          <NextButton isLoading={isSubmitSuccessful}>{nextStepText || 'Volgende stap'}</NextButton>
        </Stack>
      </Stack>
    )}
  </Layout.Content>
);
